'use client';

import { useCart } from '@/features/checkout/CartProvider';
import FlyToCart from '@/features/checkout/components/FlyToCart';
import Rating from '@/features/primitives/Rating';
import BuyBoxBell from '@/features/primitives/bell/BuyBoxBell';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Image } from 'react-bootstrap';
import { FaOpencart } from 'react-icons/fa';
import { toast } from 'react-toastify';
import './buyBox.scss';

function readMessage(messages, path, fallback = '') {
  const resolved = path
    .split('.')
    .reduce((current, segment) => current?.[segment], messages);

  return typeof resolved === 'string' ? resolved : fallback;
}

function fillTemplate(template, replacements = {}) {
  return Object.entries(replacements).reduce(
    (value, [key, replacement]) =>
      value.replaceAll(`{{${key}}}`, String(replacement)),
    String(template || ''),
  );
}

function getStockTone(stockCount) {
  if (stockCount <= 0) {
    return 'out';
  }

  if (stockCount <= 5) {
    return 'low';
  }

  return 'in';
}

function getStockLabel(messages, stockCount) {
  if (stockCount <= 0) {
    return readMessage(messages, 'buyBox.outOfStock', 'Out of stock');
  }

  if (stockCount <= 5) {
    return fillTemplate(
      readMessage(messages, 'buyBox.lowStock', 'Only {{count}} left'),
      { count: stockCount },
    );
  }

  return readMessage(messages, 'buyBox.inStock', 'In stock');
}

export default function BuyBox({
  currentUser,
  locale,
  product,
  price,
  discountedPrice,
  discountInfo,
  messages = {},
  savingsLabel = '',
}) {
  const { addItem } = useCart();
  const router = useRouter();
  const buttonRef = useRef(null);
  const qtyToggleRef = useRef(null);
  const [flyStartRect, setFlyStartRect] = useState(null);
  const [justAdded, setJustAdded] = useState(false);
  const [qty, setQty] = useState(1);
  const [openQty, setOpenQty] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(null);
  const [toggleWidth, setToggleWidth] = useState(null);

  const stockCount = Number(product?.countInStock || 0);
  const disabled = stockCount <= 0;
  const stockTone = getStockTone(stockCount);
  const stockLabel = getStockLabel(messages, stockCount);
  const maxQty = Math.max(1, stockCount || 1);
  const clampedQty = Math.min(qty, maxQty);
  const progress =
    stockCount > 0 ? Math.min((clampedQty / stockCount) * 100, 100) : 0;
  const unavailableLabel = readMessage(messages, 'unavailable', 'Unavailable');
  const normalizedLocale = String(locale || '').toLowerCase();
  const showSenegalPaymentMethods = normalizedLocale === 'fr-sn';

  const commonPaymentMethods = [
    {
      key: 'visa',
      label: 'Visa',
      src: '/payment/visa.jpg',
      variant: 'brand',
    },
    {
      key: 'mastercard',
      label: 'Mastercard',
      src: '/payment/mastercard.png',
      variant: 'brand',
    },
  ];

  const senegalPaymentMethods = [
    {
      key: 'wave',
      label: 'Wave',
      src: '/payment/wave.png',
      variant: 'mobile',
    },
    {
      key: 'orange-money',
      label: 'Orange Money',
      src: '/payment/orange_money.png',
      variant: 'mobile',
    },
    {
      key: 'yas',
      label: 'Yas',
      src: '/payment/yas.jpg',
      variant: 'mobile',
    },
  ];

  useEffect(() => {
    if (!justAdded) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setJustAdded(false), 1400);

    return () => window.clearTimeout(timeoutId);
  }, [justAdded]);

  useEffect(() => {
    setQty(1);
  }, [product?.productNumber]);

  useEffect(() => {
    if (qtyToggleRef.current) {
      setToggleWidth(qtyToggleRef.current.offsetWidth);
    }
  }, [clampedQty, openQty, product?.productNumber]);

  const handleAdd = () => {
    if (disabled) {
      return;
    }

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    addItem(product, clampedQty);
    setJustAdded(true);
    const productName = product?.name ? ` \u2014 ${product.name}` : '';
    toast.success(
      `${readMessage(messages, 'buyBox.addedToCart', 'Added to cart')}${productName}${clampedQty > 1 ? ` (${clampedQty})` : ''}.`,
    );

    if (!prefersReducedMotion && buttonRef.current) {
      setFlyStartRect(buttonRef.current.getBoundingClientRect());
      return;
    }

    router.push('/cart');
  };

  return (
    <section
      className={`buybox surface-1 buybox--${stockTone}`}
      aria-label={readMessage(messages, 'buyBox.title', 'Purchase options')}
    >
      {flyStartRect ? (
        <FlyToCart
          startRect={flyStartRect}
          reducedMotion={false}
          onFinish={() => {
            setFlyStartRect(null);
            window.setTimeout(() => router.push('/cart'), 120);
          }}
        />
      ) : null}

      <div className="buybox__header">
        <p className="buybox__eyebrow">
          {readMessage(messages, 'buyBox.title', 'Purchase options')}
        </p>

        {discountInfo?.hasDiscount ? (
          <div className="buybox__pricing">
            <strong className="buybox__price buybox__price--discounted">
              {discountedPrice || price || unavailableLabel}
            </strong>
            <div className="buybox__price-meta">
              <span className="buybox__price-original">
                {price || unavailableLabel}
              </span>
              {discountInfo?.couponLabel || savingsLabel ? (
                <span className="buybox__savings">
                  {discountInfo?.couponLabel
                    ? `${discountInfo.couponLabel}${savingsLabel ? ` • ${savingsLabel}` : ''}`
                    : savingsLabel}
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="buybox__pricing">
            <strong className="buybox__price">
              {price || unavailableLabel}
            </strong>
          </div>
        )}

        <div className="buybox__rating-row">
          <Rating
            value={product?.rating}
            numReviews={product?.numReviews}
            showText={false}
            messages={messages}
          />
          <span className={`buybox__stock buybox__stock--${stockTone}`}>
            {stockLabel}
          </span>
        </div>
      </div>

      {stockCount > 1 ? (
        <div className="buybox__progress-wrap" aria-hidden="true">
          <div className="buybox__progress">
            <div
              className="buybox__progress-bar"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      ) : null}

      <div className="buybox__controls">
        <label className="buybox__quantity-field">
          <span className="buybox__quantity-label">
            {readMessage(messages, 'buyBox.quantity', 'Quantity')}
          </span>

          {stockCount > 1 ? (
            <Dropdown
              show={openQty}
              onToggle={(isOpen) => {
                setOpenQty(isOpen);
                if (isOpen) {
                  setFocusedIdx(clampedQty - 1);
                }
              }}
              className="buybox__quantity-dropdown"
            >
              <Dropdown.Toggle
                id={`buybox-qty-toggle-${product?.productNumber || 'product'}`}
                variant="outline-primary"
                className="buybox__qty-toggle"
                aria-label={readMessage(
                  messages,
                  'buyBox.quantity',
                  'Quantity',
                )}
                ref={qtyToggleRef}
                disabled={disabled}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    const direction = event.key === 'ArrowDown' ? 1 : -1;
                    const next = Math.min(
                      Math.max((focusedIdx ?? clampedQty - 1) + direction, 0),
                      maxQty - 1,
                    );
                    setOpenQty(true);
                    setFocusedIdx(next);
                    return;
                  }

                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();

                    if (!openQty) {
                      setOpenQty(true);
                      setFocusedIdx(clampedQty - 1);
                      return;
                    }

                    const value = (focusedIdx ?? clampedQty - 1) + 1;
                    setQty(value);
                    setOpenQty(false);
                    return;
                  }

                  if (event.key === 'Escape') {
                    event.preventDefault();
                    setOpenQty(false);
                  }
                }}
              >
                {clampedQty}
              </Dropdown.Toggle>

              <Dropdown.Menu
                className="buybox__qty-menu"
                style={{ width: toggleWidth ? `${toggleWidth}px` : undefined }}
                role="listbox"
                aria-label={readMessage(
                  messages,
                  'buyBox.quantity',
                  'Quantity',
                )}
                popperConfig={{
                  placement: 'bottom',
                  modifiers: [
                    { name: 'offset', options: { offset: [0, 6] } },
                    { name: 'flip', options: { fallbackPlacements: [] } },
                  ],
                }}
              >
                {Array.from({ length: maxQty }, (_, index) => index).map(
                  (index) => (
                    <Dropdown.Item
                      key={index + 1}
                      id={`buybox-qty-option-${product?.productNumber || 'product'}-${index + 1}`}
                      active={clampedQty === index + 1 || focusedIdx === index}
                      onMouseEnter={() => setFocusedIdx(index)}
                      onClick={() => {
                        setQty(index + 1);
                        setOpenQty(false);
                      }}
                      role="option"
                      aria-selected={clampedQty === index + 1}
                    >
                      {index + 1}
                    </Dropdown.Item>
                  ),
                )}
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <div className="buybox__quantity-single">{clampedQty}</div>
          )}
        </label>

        <div className="buybox__actions">
          <BuyBoxBell
            currentUser={currentUser}
            messages={messages}
            product={product}
            qty={clampedQty}
          />

          <Button
            type="button"
            variant="transparent"
            ref={buttonRef}
            className="app-button app-button--primary buybox__cta gap-2"
            onClick={handleAdd}
            disabled={disabled}
          >
            {disabled
              ? readMessage(messages, 'buyBox.outOfStock', 'Out of stock')
              : justAdded
                ? readMessage(messages, 'buyBox.addedToCart', 'Added to cart')
                : readMessage(messages, 'buyBox.addToCart', 'Add to cart')}
            <FaOpencart size={20} aria-hidden="true" />
          </Button>
        </div>
      </div>

      <div
        className="buybox__trust mt-3"
        aria-label={readMessage(
          messages,
          'buyBox.trustLabel',
          'Purchase assurance',
        )}
      >
        <ul className="buybox__trust-list">
          <li
            className="buybox__trust-badge"
            aria-label={readMessage(
              messages,
              'buyBox.trustLabel',
              'Purchase assurance',
            )}
          >
            <Image src="/icons/quick.png" alt="" width={18} height={18} />{' '}
            <span>
              {readMessage(
                messages,
                'buyBox.fastDispatch',
                'Fast order handling',
              )}
            </span>
          </li>
          <li
            className="buybox__trust-badge"
            aria-label={readMessage(
              messages,
              'buyBox.trustLabel',
              'Purchase assurance',
            )}
          >
            <span aria-hidden="true">🔒</span>
            <span>
              {readMessage(messages, 'buyBox.securePayment', 'Secure payments')}
            </span>
          </li>

          <li
            className="buybox__trust-badge buybox__trust-badge--methods"
            aria-label={readMessage(
              messages,
              showSenegalPaymentMethods
                ? 'buyBox.supportedPayments'
                : 'buyBox.cards',
              showSenegalPaymentMethods
                ? 'Visa, Mastercard, Wave, Orange Money, Yas'
                : 'Visa, Mastercard',
            )}
          >
            <div className="buybox__payment-methods" aria-hidden="true">
              {commonPaymentMethods.map((method) => (
                <span
                  key={method.key}
                  className={`buybox__payment-chip buybox__payment-chip--${method.variant}`}
                >
                  <Image
                    src={method.src}
                    alt=""
                    width={method.variant === 'brand' ? 28 : 18}
                    height={18}
                    className="buybox__payment-chip-image"
                  />
                  <span className="buybox__payment-chip-label">
                    {method.label}
                  </span>
                </span>
              ))}
            </div>
            {showSenegalPaymentMethods ? (
              <div className="buybox__payment-methods gap-1" aria-hidden="true">
                {senegalPaymentMethods.map((method) => (
                  <span
                    key={method.key}
                    className={`buybox__payment-chip buybox__payment-chip--${method.variant}`}
                  >
                    <Image
                      src={method.src}
                      alt=""
                      width={method.variant === 'brand' ? 28 : 18}
                      height={18}
                      className="buybox__payment-chip-image"
                    />
                    <span className="buybox__payment-chip-label">
                      {method.label}
                    </span>
                  </span>
                ))}
              </div>
            ) : null}

            <span className="visually-hidden">
              {readMessage(
                messages,
                showSenegalPaymentMethods
                  ? 'buyBox.supportedPayments'
                  : 'buyBox.cards',
                showSenegalPaymentMethods
                  ? 'Visa, Mastercard, Wave, Orange Money, Yas'
                  : 'Visa, Mastercard',
              )}
            </span>
          </li>
        </ul>

        <p className="buybox__microcopy">
          {readMessage(
            messages,
            'buyBox.microcopy',
            'Need help deciding? Request a callback and we will reach out about this product.',
          )}
        </p>
      </div>
    </section>
  );
}
